/* eslint-env browser */
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'reflexbox';
import { connect } from 'react-redux';

import {
  Badge,
  Fixed,
  Toolbar,
  NavItem,
  Space,
  ButtonCircle,
  Dropdown,
  DropdownMenu,
  Arrow,
  Text,
} from 'rebass';
import Icon from 'react-geomicons';

import config from 'frontend/config';
import { API_LOCALES } from 'shared/locale';
import Rewards from 'shared/rewards';
import { DEVMODE } from 'shared/util';

import actions from 'frontend/state/actions';
import { selectors as userSelectors } from 'frontend/state/reducers/user';
import { selectors as localeSelectors } from 'frontend/state/reducers/locale';
import { selectors as siteSelectors } from 'frontend/state/reducers/site';
import { selectors as versionSelectors } from 'frontend/state/reducers/version';

import Responsive from 'frontend/components/Responsive';
import If from 'frontend/components/If';

import Logo from 'web/public/raidbots-logo.svg';

import UpdateAwareLink from './UpdateAwareLink';

class Header extends React.Component {
  static displayName = 'Header';

  static propTypes = {
    user: PropTypes.shape({
      email: PropTypes.string,
      username: PropTypes.string,
      patreonTitle: PropTypes.string,
      admin: PropTypes.object,
    }).isRequired,
    locale: PropTypes.shape({
      locale: PropTypes.string,
      active: PropTypes.bool,
      display: PropTypes.string,
    }).isRequired,
    isBetaSite: PropTypes.bool,
    newVersionAvailable: PropTypes.bool,

    onJobsClick: PropTypes.func.isRequired,
    onChangeLocale: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isBetaSite: false,
    newVersionAvailable: false,
  };

  state = {
    accountMenuOpen: false,
    localeMenuOpen: false,
  };

  toggleAccountMenu = () => {
    this.setState(prevState => ({ accountMenuOpen: !prevState.accountMenuOpen }));
  };

  toggleLocaleMenu = () => {
    this.setState(prevState => ({ localeMenuOpen: !prevState.localeMenuOpen }));
  };

  render() {
    const { user, locale, isBetaSite, newVersionAvailable, onJobsClick } = this.props;
    const reward = Rewards.getRewardsForUser(user);
    let calloutColor;
    let calloutText;
    if (isBetaSite) {
      calloutColor = config.colors.red;
      calloutText = 'Beta Build';
    } else if (DEVMODE) {
      calloutColor = config.colors.blue;
      calloutText = 'Dev Build';
    }
    return (
      <Fixed
        top
        left
        right
        zIndex={10}
        style={{
          backgroundColor: 'rgba(0, 0, 0)',
        }}
      >
        <Flex justify="center">
          <Toolbar
            style={{
              borderBottom: calloutColor ? `2px solid ${calloutColor}` : null,
              marginTop: calloutColor ? -2 : null,
              backgroundColor: null,
              maxWidth: 1200,
              width: '100%',
            }}
          >
            <Responsive size="small">
              <NavItem to="/" is={UpdateAwareLink}>
                <Flex column>
                  <Logo
                    style={{
                      width: 164,
                      height: 37,
                      marginBottom: 5,
                    }}
                  />
                  <If
                    condition={calloutText}
                    then={() => (
                      <Box
                        style={{ color: calloutColor, marginTop: -10, marginLeft: 47 }}
                      >
                        {calloutText}
                      </Box>
                    )}
                  />
                </Flex>
              </NavItem>
            </Responsive>

            <Responsive size="large">
              <NavItem to="/" is={UpdateAwareLink}>
                <Flex column>
                  <Logo
                    style={{
                      width: 164,
                      height: 37,
                      marginBottom: 5,
                    }}
                  />
                  <If
                    condition={calloutText}
                    then={() => (
                      <Box
                        style={{ color: calloutColor, marginTop: -10, marginLeft: 47 }}
                      >
                        {calloutText}
                      </Box>
                    )}
                  />
                </Flex>
              </NavItem>
            </Responsive>

            <If
              condition={newVersionAvailable}
              then={() => (
                <Box style={{ cursor: 'pointer', zIndex: 20 }}>
                  <Badge theme="secondary" onClick={() => window.location.reload(true)}>
                    Site updated, reload for latest
                  </Badge>
                </Box>
              )}
            />

            <Space auto />

            {user.email
              ? (
                <Dropdown>
                  <NavItem onClick={this.toggleAccountMenu}>
                    <Flex align="center">
                      <Box style={{ height: 16 }}>
                        {user.patreonTitle
                          ? (
                            <ButtonCircle
                              backgroundColor={reward.color}
                              color={config.colors.black}
                              size={16}
                              style={{
                                minHeight: '16px',
                                lineHeight: '16px',
                              }}
                              mr={1}
                            />
                          ) :
                          null}
                      </Box>
                      <Box style={{ textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 100 }}>
                        {user.username}
                      </Box>
                      <Box style={{ height: 19 }}>
                        <Arrow />
                      </Box>
                    </Flex>
                  </NavItem>
                  <DropdownMenu
                    right
                    onDismiss={this.toggleAccountMenu}
                    open={this.state.accountMenuOpen}
                  >
                    <NavItem is={UpdateAwareLink} to="/account" onClick={this.toggleAccountMenu}>
                      Account
                    </NavItem>
                    <If
                      condition={user.admin}
                      then={() => (
                        <NavItem href="/grommash-hold">
                          Admin
                        </NavItem>
                      )}
                    />
                    <NavItem href="/logout">
                      Logout
                    </NavItem>
                  </DropdownMenu>
                </Dropdown>
              ) :
                <NavItem is={UpdateAwareLink} to="/auth">Login</NavItem>}
            <Responsive size="large">
              <Dropdown>
                <NavItem onClick={this.toggleLocaleMenu}>
                  {locale.display}
                  <Arrow />
                </NavItem>
                <DropdownMenu
                  right
                  onDismiss={this.toggleLocaleMenu}
                  open={this.state.localeMenuOpen}
                >
                  {_.map(_.sortBy(_.filter(API_LOCALES, { active: true }), 'display'), localeInfo => (
                    <NavItem
                      key={localeInfo.locale}
                      onClick={() => {
                        this.props.onChangeLocale(localeInfo.locale);
                        this.toggleLocaleMenu();
                      }}
                    >
                      {localeInfo.display}
                    </NavItem>
                  ))}
                  <NavItem>
                    <Text color={config.colors.white} small>For item search and tooltips only</Text>
                  </NavItem>
                </DropdownMenu>
              </Dropdown>
            </Responsive>
            <NavItem onClick={onJobsClick}>
              <ButtonCircle
                backgroundColor={config.colors.orange}
                color={config.colors.black}
                size={20}
                style={{
                  minHeight: '20px',
                  lineHeight: '20px',
                }}
              >
                <Icon name="list" size="12" />
              </ButtonCircle>
            </NavItem>
          </Toolbar>
        </Flex>
      </Fixed>
    );
  }
}

const mapStateToProps = state => ({
  user: userSelectors.user(state),
  locale: localeSelectors.locale(state),
  isBetaSite: siteSelectors.isBetaSite(state),
  newVersionAvailable: versionSelectors.newVersionAvailable(state),
});

const mapDispatchToProps = {
  onChangeLocale: actions.selectLocale,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
